/**
 * PlaceCoreIntentsSection — "Passar bra för" chips (max 5).
 * Hidden if fewer than 2 intents (prefer silence over weak signal).
 * Icons resolved from intent key.
 */
import { Badge } from "@/components/ui/badge";
import { Users, TreePine, Moon, Mountain, Palette, Compass, Heart } from "lucide-react";
import { useTranslate } from "@/i18n";

const INTENT_ICONS: Record<string, React.ReactNode> = {
  "stillhet-ro": <Heart className="h-3.5 w-3.5" />,
  "natur-friluftsliv": <TreePine className="h-3.5 w-3.5" />,
  "utflykt": <Compass className="h-3.5 w-3.5" />,
  "familj": <Users className="h-3.5 w-3.5" />,
  "vandring": <Mountain className="h-3.5 w-3.5" />,
  "kultur-historia": <Palette className="h-3.5 w-3.5" />,
  "lugn-helg": <Moon className="h-3.5 w-3.5" />,
  // Legacy keys
  stillness: <Heart className="h-3.5 w-3.5" />,
  nature: <TreePine className="h-3.5 w-3.5" />,
  excursion: <Compass className="h-3.5 w-3.5" />,
  family: <Users className="h-3.5 w-3.5" />,
  hiking: <Mountain className="h-3.5 w-3.5" />,
  culture: <Palette className="h-3.5 w-3.5" />,
  calm_weekend: <Moon className="h-3.5 w-3.5" />,
};

/** Canonical display order */
const INTENT_ORDER = ["stillhet-ro", "natur-friluftsliv", "utflykt", "familj", "vandring", "kultur-historia", "lugn-helg", "stillness", "nature", "excursion", "family", "hiking", "culture", "calm_weekend"];

interface Props {
  chips: { key: string; label: string }[];
}

export function PlaceCoreIntentsSection({ chips }: Props) {
  const t = useTranslate();
  if (chips.length < 2) return null;

  const sorted = [...chips]
    .sort((a, b) => {
      const ia = INTENT_ORDER.indexOf(a.key);
      const ib = INTENT_ORDER.indexOf(b.key);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    })
    .slice(0, 5);

  return (
    <section>
      <h2 className="font-display text-lg text-foreground mb-3">
        {t("place.intents.heading")}
      </h2>
      <div className="flex flex-wrap gap-2">
        {sorted.map((chip) => (
          <Badge
            key={chip.key}
            variant="secondary"
            className="text-sm font-normal px-3 py-1.5 gap-1.5 rounded-full"
          >
            {INTENT_ICONS[chip.key] ?? <Compass className="h-3.5 w-3.5" />}
            {t(`intent.${chip.key}`) || chip.label}
          </Badge>
        ))}
      </div>
    </section>
  );
}
